/** @jsx jsx */
import { jsx } from '@front10/base-elements';
import React, { useState, useContext } from 'react';
import Box from '../../../elements/Box';
import { TabList, TabPanels, TabPanel, Tabs } from '@reach/tabs';
import { Tab, useTabsContext } from '@reach/tabs';
import ArticleHeadItem from './ArticleHeadItem';
import ArticleBodyItem from './ArticleBodyItem';
import { Context } from '@front10/helpers/dist/Context';
import Link from '../../../elements/Link';

import { isInInterval, isValidDeviceSetting, transformLabel } from './../utils/utils';

const KEY_ARROW_LEFT = 37;
const KEY_ARROW_UP = 38;
const KEY_ARROW_RIGHT = 39;
const KEY_ARROW_DOWN = 40;
const KEY_ENTER = 13;
const KEY_SPACE = 32;

const Articles = (props) => {
  const [isShowPanels, setIsShowPanels] = useState(false);
  const [tabIndex, setTabIndex] = React.useState(-1);
  const onClickTabAction = React.useRef(false);
  const { translate } = useContext(Context);
  const {
    data,
    containerSettings,
    selectedTabBarSettings,
    tabSettings,
    articleHeaderSettings,
    tabListSettings,
    articleBodySettings: baseArticleBodySettings,
    articleBodySettingsMobile = {},
    articleBodySettingsTablet = {},
    articleBodySettingsDesktop = {},
    deviceViewPortSize,
    isMobile,
    containerWidth,
    scrollIntoViewSettings = {
      useNativeFunction: undefined
    },
    dispatch
  } = props;

  let articleBodySettings = {
    ...baseArticleBodySettings
  };

  if (deviceViewPortSize !== null && deviceViewPortSize !== undefined && isMobile) {
    const { mobile, desktop, tablet } = deviceViewPortSize;
    if (isValidDeviceSetting(mobile) && isInInterval(containerWidth, mobile)) {
      articleBodySettings = {
        ...baseArticleBodySettings,
        ...articleBodySettingsMobile
      };
    } else if (isValidDeviceSetting(tablet) && isInInterval(containerWidth, tablet)) {
      articleBodySettings = {
        ...baseArticleBodySettings,
        ...articleBodySettingsTablet
      };
    } else if (isValidDeviceSetting(desktop) && isInInterval(containerWidth, desktop)) {
      articleBodySettings = {
        ...baseArticleBodySettings,
        ...articleBodySettingsDesktop
      };
    }
  }

  let tabPanels = [];
  const articleRef = React.useRef();
  const containerRef = React.useRef();
  const [containerTabIndex, setContainerTabIndex] = React.useState(-1);
  const scrollIntoViewContent = () => {
    setTimeout(
      () => {
        if (containerRef?.current !== null && containerRef?.current !== undefined) {
          if (
            scrollIntoViewSettings.useNativeFunction === undefined ||
            scrollIntoViewSettings.useNativeFunction === null
          ) {
            containerRef?.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
          } else if (scrollIntoViewSettings.useNativeFunction === true) {
            window.scroll({
              top: containerRef?.current?.getBoundingClientRect().top + window.scrollY,
              left: 0,
              behavior: 'smooth'
            });
          }
        }
      },
      scrollIntoViewSettings.delayToScrollToTab !== undefined
        ? scrollIntoViewSettings.delayToScrollToTab
        : 300
    );
    setTimeout(
      () => {
        setContainerTabIndex(0);
        if (containerRef?.current !== null && containerRef?.current !== undefined) {
          containerRef?.current?.focus();
        }
      },
      scrollIntoViewSettings.delayToFocusTab !== undefined
        ? scrollIntoViewSettings.delayToFocusTab
        : 750
    );
  };
  const setOnClickTabActionFn = () => {
    onClickTabAction.current = true;
  };
  const handleTabsChange = (index) => {
    const newIsShowPanels = tabIndex === index ? (isShowPanels ? false : true) : true;
    setIsShowPanels(newIsShowPanels);
    setTabIndex(index);
    if (newIsShowPanels && onClickTabAction.current) {
      setTimeout(
        () => {
          if (articleRef?.current !== null && articleRef?.current !== undefined) {
            if (
              scrollIntoViewSettings.useNativeFunction === undefined ||
              scrollIntoViewSettings.useNativeFunction === null
            ) {
              articleRef?.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
            } else if (scrollIntoViewSettings.useNativeFunction === true) {
              window.scroll({
                top: articleRef?.current?.getBoundingClientRect().top + window.scrollY,
                left: 0,
                behavior: 'smooth'
              });
            }
          }
          onClickTabAction.current = false;
        },
        scrollIntoViewSettings.delayToScrollToPanel !== undefined
          ? scrollIntoViewSettings.delayToScrollToPanel
          : 300
      );
    }
  };

  React.useEffect(() => {
    setTimeout(() => {
      const tabs = document.querySelectorAll('button[role=tab]');
      if (tabs && tabs.length > 0) {
        tabs.forEach(function (tab) {
          tab.setAttribute('aria-selected', false);
        });
      }
    }, 200);
  }, []);

  function CustomTab({
    index,
    isShowPanels,
    tabIndex,
    selectedTabBarSettings,
    tabSettings,
    ...props
  }) {
    const { focusedIndex, id } = useTabsContext();
    let style = { ...tabSettings };
    let newProps = { ...props };
    if (tabIndex === index && isShowPanels) {
      style = { ...tabSettings, ...selectedTabBarSettings };
    } else {
      newProps = { ...props, 'aria-controls': undefined };
    }
    return (
      <Tab
        {...newProps}
        onKeyDown={(e) => {
          if (isShowPanels && e.keyCode === KEY_SPACE) {
            e.preventDefault();
          }
          if (
            e.keyCode === KEY_ARROW_RIGHT ||
            e.keyCode === KEY_ARROW_DOWN ||
            e.keyCode === KEY_ARROW_LEFT ||
            e.keyCode === KEY_ARROW_UP
          ) {
            if (data && data.length > 1) {
              if (e.keyCode === KEY_ARROW_RIGHT) {
                dispatch({
                  type: 'SELECT_ARTICLE',
                  payload: {
                    category: 'Article',
                    selected: transformLabel(
                      data[index + 1 > data.length - 1 ? 0 : index + 1].article2Title
                    ),
                    eventType: 'key press'
                  }
                });
              } else if (e.keyCode === KEY_ARROW_LEFT) {
                dispatch({
                  type: 'SELECT_ARTICLE',
                  payload: {
                    category: 'Article',
                    selected: transformLabel(
                      data[index - 1 >= 0 ? index - 1 : data.length - 1].article2Title
                    ),
                    eventType: 'key press'
                  }
                });
              } else if (e.keyCode === KEY_ARROW_DOWN) {
                e.preventDefault();
                wasMakedForceClick.current = true;
                const ptr = index + 1 > data.length - 1 ? 0 : index + 1;
                const tmp = document.getElementById(`tab-list-articles-container--tab--${ptr}`);
                tmp.click();
                dispatch({
                  type: 'SELECT_ARTICLE',
                  payload: {
                    category: 'Article',
                    selected: transformLabel(data[ptr].article2Title),
                    eventType: 'key press'
                  }
                });
              } else if (e.keyCode === KEY_ARROW_UP) {
                e.preventDefault();
                wasMakedForceClick.current = true;
                const ptr = index - 1 >= 0 ? index - 1 : data.length - 1;
                const tmp = document.getElementById(`tab-list-articles-container--tab--${ptr}`);
                tmp.click();
                dispatch({
                  type: 'SELECT_ARTICLE',
                  payload: {
                    category: 'Article',
                    selected: transformLabel(data[ptr].article2Title),
                    eventType: 'key press'
                  }
                });
              }
            } else if (data && data.length === 1) {
              if (!isShowPanels) {
                if (e.keyCode === KEY_ARROW_RIGHT) {
                  dispatch({
                    type: 'SELECT_ARTICLE',
                    payload: {
                      category: 'Article',
                      selected: transformLabel(data[index].article2Title),
                      eventType: 'key press'
                    }
                  });
                } else if (e.keyCode === KEY_ARROW_LEFT) {
                  dispatch({
                    type: 'SELECT_ARTICLE',
                    payload: {
                      category: 'Article',
                      selected: transformLabel(data[index].article2Title),
                      eventType: 'key press'
                    }
                  });
                } else if (e.keyCode === KEY_ARROW_DOWN) {
                  e.preventDefault();
                  wasMakedForceClick.current = true;
                  const tmp = document.getElementById(`tab-list-articles-container--tab--${index}`);
                  tmp.click();
                  dispatch({
                    type: 'SELECT_ARTICLE',
                    payload: {
                      category: 'Article',
                      selected: transformLabel(data[index].article2Title),
                      eventType: 'key press'
                    }
                  });
                } else if (e.keyCode === KEY_ARROW_UP) {
                  e.preventDefault();
                  wasMakedForceClick.current = true;
                  const tmp = document.getElementById(`tab-list-articles-container--tab--${index}`);
                  tmp.click();
                  dispatch({
                    type: 'SELECT_ARTICLE',
                    payload: {
                      category: 'Article',
                      selected: transformLabel(data[index].article2Title),
                      eventType: 'key press'
                    }
                  });
                }
              } else {
                e.preventDefault();
                e.stopPropagation();
              }
            }
          } else if (e.keyCode === KEY_ENTER) {
            wasMakedForceClick.current = true;
            dispatch({
              type: 'SELECT_ARTICLE',
              payload: {
                category: 'Article',
                selected: transformLabel(data[index].article2Title),
                eventType: 'key press'
              }
            });
          }
        }}
        onFocus={() => {
          if (!isShowPanels) {
            const tmp = document.getElementById(`${id}--tab--${focusedIndex}`);
            if (tmp) {
              tmp.setAttribute('aria-selected', false);
            }
          }
        }}
        style={style}
      />
    );
  }

  const wasMakedForceClick = React.useRef(false);

  const handlerClick = (e, index) => {
    dispatch({
      type: 'SELECT_ARTICLE',
      payload: {
        category: 'Article',
        selected: transformLabel(data[index].article2Title),
        eventType: 'click'
      }
    });
  };

  return (
    <Box
      {...containerSettings}
      className="fr-container-articles"
      tabIndex={containerTabIndex}
      onBlur={() => {
        setContainerTabIndex(-1);
      }}
      focusReferences={(e) => {
        containerRef.current = e;
      }}
      dataTestId="travelsGuideSecondSection"
    >
      <Tabs
        keyboardActivation="auto"
        onChange={handleTabsChange}
        arial-label={translate('cmp-travel-guide-articles-tab-list-aria-label', 'Articles')}
        id="tab-list-articles-container"
      >
        <TabList style={tabListSettings}>
          {data.map((article, index) => {
            tabPanels.push(
              <TabPanel tabIndex={-1}>
                <ArticleBodyItem
                  dispatch={dispatch}
                  scrollIntoViewContent={scrollIntoViewContent}
                  scrollIntoViewSettings={scrollIntoViewSettings}
                  {...article}
                  {...articleBodySettings}
                ></ArticleBodyItem>
              </TabPanel>
            );
            return (
              <CustomTab
                index={index}
                sx={{
                  flex: 1,
                  backgroundColor: 'transparent',
                  border: 'none',
                  padding: '0px',
                  textAlign: 'unset'
                }}
                tabIndex={tabIndex}
                selectedTabBarSettings={selectedTabBarSettings}
                tabSettings={tabSettings}
                isShowPanels={isShowPanels}
              >
                {scrollIntoViewSettings.useNativeFunction === false ? (
                  <Link
                    onClick={(e) => {
                      setOnClickTabActionFn();
                    }}
                    href="#tab-panel-article-container"
                    className="fr-link-article"
                    target="_self"
                  >
                    <ArticleHeadItem
                      {...article}
                      {...articleHeaderSettings}
                      isSelected={index === tabIndex}
                      setOnClickTabAction={() => {}}
                      dispatch={dispatch}
                      handlerClick={handlerClick}
                      index={index}
                    ></ArticleHeadItem>
                  </Link>
                ) : (
                  <ArticleHeadItem
                    {...article}
                    {...articleHeaderSettings}
                    isSelected={index === tabIndex}
                    setOnClickTabAction={setOnClickTabActionFn}
                    dispatch={dispatch}
                    handlerClick={handlerClick}
                    index={index}
                  ></ArticleHeadItem>
                )}
              </CustomTab>
            );
          })}
        </TabList>
        <Box
          focusReferences={(x) => {
            articleRef.current = x;
          }}
          className="fr-tabPanel-container"
          id="tab-panel-article-container"
        >
          {isShowPanels && (
            <TabPanels>
              {tabPanels.map((tab) => {
                return tab;
              })}
            </TabPanels>
          )}
        </Box>
      </Tabs>
    </Box>
  );
};

export default Articles;
